import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import { Roster } from '@services/rosters/types/roster';

import { RosterRelation } from '@services/rosters/types/roster-relation';

import AppState from '@webapp/store/state';
import { UpdateRoster } from '@webapp/store/rosters/action-creators';

import styled from 'styled-components';

import RosterOperators from './roster-operators';
import Box from '@components/layout/box';
import Avatar from '@components/basics/avatar';
import BoxHeader from '@components/layout/box-header';
import ArrowIcon from '@components/basics/icons/arrow';
import Flex from '@components/layout/flex';
import EditButton from '@components/basics/edit-button';
import RosterMenu from '@webapp/controllers/menus/roster-menu';
import { RosterOperator } from '@services/rosters/types/roster-operator';

interface RosterBoxProps {
  className?: string;
  noBorderTop?: boolean;
  noBorderBottom?: boolean;
}

interface StateProps {
  rosters: Roster[];
  relations: RosterRelation[];
  operators: RosterOperator[];
  updateRoster: (roster: Roster) => UpdateRoster;
}

const mapStateToProps = (state: AppState) => ({
  rosters: state.rosters.list,
  relations: state.rosters.artists,
  operators: state.rosters.operators,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateRoster: (roster: Roster) => dispatch(new UpdateRoster(roster)),
});

export const RosterBox: React.FC<RosterBoxProps & StateProps> = ({
  rosters,
  relations,
  ...props
}) => {
  const [openRoster, setOpenRoster] = React.useState('');

  return (
    <div>
      {rosters.map((roster) => (
        <Wrapper>
          <Box
            noBorderTop={props.noBorderTop}
            noBorderBottom={openRoster === roster.id}
          >
            <BoxHeader
              noMargin
              title={roster.name}
              text={
                <Translate
                  id="USER_PROFILE.ROSTER.ARTISTS"
                  data={{ amount: relations?.[roster.id]?.length || 0 }}
                />
              }
              customAction={
                <Flex justifyContent="flex-end" alignItems="center" gap={5}>
                  <RosterMenu action={<EditButton />} roster={roster} />
                  <ArrowWrapper
                    onClick={() => {
                      if (!openRoster) {
                        setOpenRoster(roster.id);
                      } else {
                        setOpenRoster('');
                      }
                    }}
                  >
                    <ArrowIcon color="dark" up={roster.id !== openRoster} />
                  </ArrowWrapper>
                </Flex>
              }
            />
          </Box>
          {roster.id === openRoster && (
            <>
              <Box noBorderTop noBorderBottom={props.noBorderBottom}>
                <BoxHeader
                  title={<Translate id="USER_PROFILE.ROSTER.ARTIST.TITLE" />}
                />
                <AvatarList>
                  {(relations[roster.id] || []).map((artist) => (
                    <Avatar
                      className="avatar"
                      src={artist.artistAvatar}
                      key={artist.artistId}
                    />
                  ))}
                </AvatarList>
              </Box>
              <Box noBorderBottom={props.noBorderBottom} noBorderTop>
                <BoxHeader
                  title={<Translate id="USER_PROFILE.ROSTER.OPERATOR.TITLE" />}
                  text={<Translate id="USER_PROFILE.ROSTER.OPERATOR.TEXT" />}
                />
                <OperatorsList>
                  <RosterOperators roster={roster} ownerId={roster.labelId} />
                </OperatorsList>
              </Box>
            </>
          )}
        </Wrapper>
      ))}
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(RosterBox)`
  margin-bottom: 20px;
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    margin-bottom: 30px;
  }
  &:last-child {
    margin-bottom: 0;
  }
  .error-message {
    color: ${({ theme }) => theme.colors.error};
    font-weight: 700;
    font-size: 12px;
  }
  .error-color {
    color: ${({ theme }) => theme.colors.error};
  }
  .mt-5 {
    margin-top: 5px;
  }
`);

const ArrowWrapper = styled.div`
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};
`;

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;
  margin-bottom: 10px;
`;

const AvatarList = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 10px;
  align-items: center;
  justify-items: center;
  grid-template-columns: repeat(4, minmax(48px, 1fr));

  .avatar {
    width: 48px;
    height: 48px;
    flex-grow: 0;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    grid-template-columns: repeat(8, minmax(48px, 1fr));
  }
`;

const OperatorsList = styled.div`
  display: grid;
  grid-gap: 10px;
`;
